import { useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

const STEPS = ['code', 'details', 'done'];

export default function RegisterPage({ onBackToLogin }) {
  const { signUp } = useAuth();

  const [step,     setStep]     = useState('code');    // code → details → done
  const [invCode,  setInvCode]  = useState('');
  const [org,      setOrg]      = useState(null);      // { org_id, org_name }
  const [form,     setForm]     = useState({ name: '', email: '', password: '', confirm: '' });
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  // ── Step 1: Validate invite code ──────────────────────────────
  const validateCode = async () => {
    if (!invCode.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: rpcErr } = await supabase
        .rpc('validate_invite_code', { p_code: invCode.trim().toUpperCase() });

      if (rpcErr) throw rpcErr;

      const result = data?.[0];
      if (!result || !result.is_valid) {
        setError(result?.message || 'Invalid invite code. Please check and try again.');
      } else {
        setOrg({ org_id: result.org_id, org_name: result.org_name });
        setStep('details');
      }
    } catch (err) {
      setError(err.message || 'Could not verify invite code. Please try again.');
    }
    setLoading(false);
  };

  // ── Step 2: Create account ─────────────────────────────────────
  const createAccount = async () => {
    setError('');
    if (!form.name.trim())                        return setError('Please enter your full name.');
    if (!form.email.trim())                       return setError('Please enter your email address.');
    if (form.password.length < 8)                 return setError('Password must be at least 8 characters.');
    if (form.password !== form.confirm)           return setError('Passwords do not match.');

    setLoading(true);
    try {
      // Create Supabase Auth user
      // meta is picked up by the handle_new_user trigger → sets profile.org_id + role
      const { data, error: signUpErr } = await signUp(form.email, form.password, {
        name:   form.name,
        role:   'Admin',
        org_id: org.org_id,
      });

      if (signUpErr) throw signUpErr;

      // Mark invite code as used so nobody else can register with it
      await supabase
        .from('organizations')
        .update({ invite_code_used: true })
        .eq('id', org.org_id);

      setStep('done');
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    }
    setLoading(false);
  };

  // ─────────────────────────────────────────────────────────────
  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: '#060b10',
    }}>
      {/* Background grid */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.03,
        backgroundImage: 'linear-gradient(#00e5a0 1px,transparent 1px),linear-gradient(90deg,#00e5a0 1px,transparent 1px)',
        backgroundSize: '40px 40px',
      }}/>

      <div style={{ position: 'relative', width: '100%', maxWidth: 420, padding: 20 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: 14, marginBottom: 14, background: '#00e5a018', border: '1.5px solid #00e5a030', fontSize: 26 }}>📡</div>
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 24, color: '#e8edf2' }}>
            RFID <span style={{ color: '#00e5a0' }}>WMS</span>
          </div>
          <div style={{ fontSize: 12, color: '#3a5060', marginTop: 5 }}>Create your organization account</div>
        </div>

        {/* Progress steps */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 28 }}>
          {[
            { id: 'code',    label: 'Invite Code' },
            { id: 'details', label: 'Your Details' },
            { id: 'done',    label: 'All Done!' },
          ].map((s, i) => {
            const idx    = STEPS.indexOf(step);
            const sIdx   = STEPS.indexOf(s.id);
            const active = s.id === step;
            const done   = sIdx < idx;
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700,
                    background: done ? '#00e5a0' : active ? '#00e5a018' : '#1a2a3a',
                    border: `2px solid ${done || active ? '#00e5a0' : '#1a2a3a'}`,
                    color: done ? '#060b10' : active ? '#00e5a0' : '#3a5060',
                  }}>
                    {done ? '✓' : i + 1}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: active ? '#c8d6e0' : '#3a5060' }}>{s.label}</span>
                </div>
                {i < 2 && <div style={{ width: 24, height: 1, background: '#1a2a3a' }}/>}
              </div>
            );
          })}
        </div>

        {/* Card */}
        <div style={{ background: '#0d1520', border: '1px solid #1a2a3a', borderRadius: 14, padding: 28 }}>

          {/* ── STEP 1: Invite code ── */}
          {step === 'code' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 16, color: '#e8edf2', marginBottom: 6 }}>Enter your invite code</div>
                <div style={{ fontSize: 12, color: '#607080' }}>Your invite code was provided by your system administrator. It looks like <span style={{ fontFamily: 'monospace', color: '#00e5a0' }}>ORG-XXXX-XXXX</span></div>
              </div>

              <div>
                <input
                  style={{
                    width: '100%', padding: '12px 16px', background: '#0a1018',
                    border: '1px solid #1a2a3a', borderRadius: 8, color: '#e8edf2',
                    fontFamily: "'IBM Plex Mono',monospace", fontSize: 16, fontWeight: 700,
                    outline: 'none', textAlign: 'center', letterSpacing: '2px',
                    textTransform: 'uppercase',
                  }}
                  placeholder="ORG-XXXX-XXXX"
                  value={invCode}
                  onChange={e => {
                    setError('');
                    // Auto-format: insert dash after ORG-
                    let v = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
                    setInvCode(v);
                  }}
                  onKeyDown={e => e.key === 'Enter' && validateCode()}
                  maxLength={13}
                  autoFocus
                />
              </div>

              {error && <ErrorBox msg={error} />}

              <button
                onClick={validateCode}
                disabled={loading || invCode.length < 8}
                style={{ ...btnStyle('#00e5a0', '#060b10'), opacity: (loading || invCode.length < 8) ? 0.5 : 1 }}>
                {loading ? 'Verifying…' : 'Verify Code →'}
              </button>
            </div>
          )}

          {/* ── STEP 2: Account details ── */}
          {step === 'details' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Org confirmation banner */}
              <div style={{ background: '#00e5a010', border: '1px solid #00e5a025', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>🏢</span>
                <div>
                  <div style={{ fontSize: 11, color: '#3a5060', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Joining organization</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#00e5a0' }}>{org?.org_name}</div>
                </div>
                <div style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, background: '#00e5a018', color: '#00e5a0', border: '1px solid #00e5a030', borderRadius: 6, padding: '2px 8px' }}>ADMIN</div>
              </div>

              <div>
                <Lbl>Full Name</Lbl>
                <input style={inpStyle} placeholder="Ahmad Razif" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
              </div>
              <div>
                <Lbl>Work Email</Lbl>
                <input style={inpStyle} type="email" placeholder="ahmad@company.com" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <Lbl>Password</Lbl>
                <input style={inpStyle} type="password" placeholder="Min 8 characters" value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              </div>
              <div>
                <Lbl>Confirm Password</Lbl>
                <input style={inpStyle} type="password" placeholder="Repeat password" value={form.confirm}
                  onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && createAccount()} />
              </div>

              {/* Password strength hint */}
              {form.password.length > 0 && (
                <PasswordStrength password={form.password} />
              )}

              {error && <ErrorBox msg={error} />}

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setStep('code'); setError(''); }}
                  style={{ ...btnStyle('none', '#607080'), border: '1px solid #1a2a3a', flex: 1 }}>← Back</button>
                <button onClick={createAccount} disabled={loading}
                  style={{ ...btnStyle('#00e5a0', '#060b10'), flex: 2, opacity: loading ? 0.6 : 1 }}>
                  {loading ? 'Creating account…' : 'Create Account'}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: Done ── */}
          {step === 'done' && (
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#00e5a018', border: '2px solid #00e5a0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>✓</div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 18, color: '#e8edf2' }}>Account Created!</div>
              <div style={{ fontSize: 13, color: '#607080', lineHeight: 1.6 }}>
                Welcome to <span style={{ color: '#00e5a0', fontWeight: 700 }}>{org?.org_name}</span>.<br/>
                Check your email to confirm your address, then sign in.
              </div>
              <div style={{ background: '#f5a62310', border: '1px solid #f5a62330', borderRadius: 8, padding: '10px 14px', fontSize: 11, color: '#f5a623', width: '100%' }}>
                📧 A confirmation email was sent. Click the link to activate your account before logging in.
              </div>
              <button onClick={onBackToLogin} style={{ ...btnStyle('#00e5a0', '#060b10'), width: '100%', marginTop: 4 }}>
                Go to Login →
              </button>
            </div>
          )}
        </div>

        {/* Back to login link */}
        {step !== 'done' && (
          <div style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: '#3a5060' }}>
            Already have an account?{' '}
            <button onClick={onBackToLogin}
              style={{ background: 'none', border: 'none', color: '#00e5a0', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
              Sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Small helpers ──────────────────────────────────────────────────
function Lbl({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: '#3a5060', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>
      {children}
    </div>
  );
}

function ErrorBox({ msg }) {
  return (
    <div style={{ background: '#e0575718', border: '1px solid #e0575730', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#e05757' }}>
      {msg}
    </div>
  );
}

function PasswordStrength({ password }) {
  const checks = [
    { label: '8+ characters',         pass: password.length >= 8 },
    { label: 'Uppercase letter',       pass: /[A-Z]/.test(password) },
    { label: 'Number',                 pass: /[0-9]/.test(password) },
    { label: 'Special character',      pass: /[^A-Za-z0-9]/.test(password) },
  ];
  const score  = checks.filter(c => c.pass).length;
  const colors = ['#e05757', '#f5a623', '#f5a623', '#00e5a0', '#00e5a0'];
  const labels = ['Weak', 'Fair', 'Fair', 'Strong', 'Very strong'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i < score ? colors[score] : '#1a2a3a', transition: 'background 0.2s' }}/>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {checks.map(c => (
            <span key={c.label} style={{ fontSize: 10, color: c.pass ? '#00e5a0' : '#3a5060', display: 'flex', alignItems: 'center', gap: 3 }}>
              <span>{c.pass ? '✓' : '○'}</span> {c.label}
            </span>
          ))}
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, color: colors[score] }}>{labels[score]}</span>
      </div>
    </div>
  );
}

const inpStyle = {
  width: '100%', padding: '9px 12px', background: '#0a1018',
  border: '1px solid #1a2a3a', borderRadius: 8, color: '#e8edf2',
  fontFamily: 'inherit', fontSize: 13, outline: 'none',
};

const btnStyle = (bg, color) => ({
  padding: '11px 0', borderRadius: 8, border: 'none',
  background: bg, color, fontFamily: 'inherit',
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
  width: '100%', transition: 'opacity 0.15s',
});
